import { resetAIAnalystInitialized } from '@/app/ai/atoms/aiAnalystAtoms';
import { debugFlag } from '@/app/debugFlags/debugFlags';
import { fileViewState } from '@/app/fileViewState/fileViewState';
import { startupTimer } from '@/app/gridGL/helpers/startupTimer';
import { htmlCellsHandler } from '@/app/gridGL/HTMLGrid/htmlCells/htmlCellsHandler';
import { loadAssets } from '@/app/gridGL/loadAssets';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { thumbnail } from '@/app/gridGL/pixiApp/thumbnail';
import { isNoMultiplayer } from '@/app/helpers/isEmbed';
import { getPreloadState, preloadLanguageWorkers, preloadStateAtom, waitForPreload } from '@/app/preload/preload';
import initCoreClient from '@/app/quadratic-core/quadratic_core';
import { VersionComparisonResult, compareVersions } from '@/app/schemas/compareVersions';
import { showStockDataPanelAtom } from '@/app/ui/menus/AIAnalyst/stockDataPanelAtom';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { renderWebWorker } from '@/app/web-workers/renderWebWorker/renderWebWorker';
import { initCoreWorkers } from '@/app/web-workers/workers';
import { authClient } from '@/auth/auth';
import { scheduledTasksAtom } from '@/jotai/scheduledTasksAtom';
import { apiClient } from '@/shared/api/apiClient';
import { loadClientDataKv } from '@/shared/atom/clientDataKvAtom';
import { updateTeamBilling } from '@/shared/atom/teamBillingAtom';
import { ROUTES } from '@/shared/constants/routes';
import { registerEventAnalyticsData } from '@/shared/utils/analyticsEvents';
import { sendAnalyticsError } from '@/shared/utils/error';
import { handleSentryReplays } from '@/shared/utils/sentry';
import { updateRecentFiles } from '@/shared/utils/updateRecentFiles';
import { captureEvent } from '@sentry/react';
import { atom, getDefaultStore } from 'jotai';
import { FilePermissionSchema, type ApiTypes } from 'quadratic-shared/typesAndSchemas';

// Tracks the current load operation to detect stale completions
let currentLoadId = 0;

export type FileData = ApiTypes['/v0/files/:uuid.GET.response'];

export type FileLoadingStatus = 'empty' | 'loading' | 'loaded' | 'error';

export interface FileLoadingProgress {
  loaded: number;
  total: number | null;
}

export interface FileLoadingState {
  uuid: string | null;
  status: FileLoadingStatus;
  data: FileData | null;
  error: string | null;
  isVersionHistoryPreview: boolean;
  sequenceNumber: number | undefined;
}

const defaultFileLoadingState: FileLoadingState = {
  uuid: null,
  status: 'empty',
  data: null,
  error: null,
  isVersionHistoryPreview: false,
  sequenceNumber: undefined,
};

export const fileLoadingAtom = atom<FileLoadingState>(defaultFileLoadingState);

// Progress for the in-flight file fetch lives in its own atom so that the
// many progress updates that arrive while streaming the file body don't
// cascade re-renders to every consumer of `fileLoadingAtom` (TopBar,
// useFileRouteLoaderData, useFileLoadingDomMirror, etc.). Only the loading
// overlay subscribes to this.
export const fileLoadingProgressAtom = atom<FileLoadingProgress | null>(null);

export interface LoadFileOptions {
  uuid: string;
  sequenceNumber?: number;
  updateBilling?: boolean;
  requestUrl?: string;
}

export interface LoadFileResult {
  success: boolean;
  redirectUrl?: string;
  error?: string;
}

/**
 * Loads a file into the application.
 * This handles all the initialization that was previously in the file route loader.
 */
export const loadFile = async (options: LoadFileOptions): Promise<LoadFileResult> => {
  const { uuid, sequenceNumber, updateBilling = false, requestUrl } = options;
  const store = getDefaultStore();
  const currentState = store.get(fileLoadingAtom);

  const isVersionHistoryPreview = sequenceNumber !== undefined;

  // If the same file and version is already loaded, skip reloading
  if (
    currentState.uuid === uuid &&
    currentState.status === 'loaded' &&
    currentState.sequenceNumber === sequenceNumber
  ) {
    return { success: true };
  }

  // Increment load ID to track this specific load operation.
  // This allows us to detect when a newer load has started and abort stale operations.
  const thisLoadId = ++currentLoadId;

  // Helper to check if this load operation is still current
  const isStaleLoad = () => currentLoadId !== thisLoadId;

  // Determine if we need to reset:
  // 1. Switching to a different file (need full reset)
  // 2. Loading a version history preview of same file (need to disconnect multiplayer)
  const needsReset = currentState.uuid !== null && (currentState.uuid !== uuid || isVersionHistoryPreview);

  if (needsReset) {
    const { resetCodeEditorState } = await import('@/app/atoms/codeEditorAtom');
    resetCodeEditorState();
    resetAIAnalystInitialized();
    htmlCellsHandler.clearCache();
    pixiApp.resetForNewFile();
    renderWebWorker.reset();

    // Reset panel states that are file-specific (only when switching to a different file)
    if (currentState.uuid !== uuid) {
      const { fileHistoryAtom } = await import('@/app/atoms/fileHistoryAtom');
      store.set(fileHistoryAtom, { show: false, checkpoints: [], activeSequenceNum: null, isLoading: false });
      store.set(showStockDataPanelAtom, false);
      store.set(scheduledTasksAtom, (prev) => ({ ...prev, show: false, currentTaskId: null, tasks: [] }));
    }
    const { multiplayer } = await import('@/app/web-workers/multiplayerWebWorker/multiplayer');
    if (currentState.uuid !== uuid) {
      // Use switchFile() for graceful connection release - the worker stays alive
      // and pending transactions will be drained before the old connection closes
      multiplayer.switchFile();
    } else if (isVersionHistoryPreview) {
      multiplayer.disconnect();
    }
    const { pythonWebWorker } = await import('@/app/web-workers/pythonWebWorker/pythonWebWorker');
    const { javascriptWebWorker } = await import('@/app/web-workers/javascriptWebWorker/javascriptWebWorker');
    pythonWebWorker.restart();
    javascriptWebWorker.restart();
    await quadraticCore.reset();
  }

  // Set loading state
  store.set(fileLoadingAtom, {
    uuid,
    status: 'loading',
    data: null,
    error: null,
    isVersionHistoryPreview,
    sequenceNumber,
  });
  store.set(fileLoadingProgressAtom, { loaded: 0, total: null });

  startupTimer.reset();
  startupTimer.start('file.loader');

  // Load file data from API (can run in parallel with preload)
  const loadFileFromApi = async (): Promise<FileData | null> => {
    try {
      startupTimer.start('file.loader.files.get');
      const data = await apiClient.files.get(uuid, { updateBilling });
      startupTimer.end('file.loader.files.get');

      if (debugFlag('debugShowMultiplayer') || debugFlag('debugShowFileIO')) {
        console.log(
          `[File API] Received information for file ${uuid} with sequence_num ${data.file.lastCheckpointSequenceNumber}.`
        );
      }
      return data;
    } catch (error: unknown) {
      const isLoggedIn = await authClient.isAuthenticated();
      const errorWithStatus = error as { status?: number };

      if (errorWithStatus.status === 403 && !isLoggedIn) {
        // Need to redirect to login
        return null;
      }
      if (!isVersionHistoryPreview) {
        updateRecentFiles(uuid, '', false);
      }
      throw error;
    }
  };

  // Wait for preload and initialize any missing components
  const ensurePreloadComplete = async () => {
    // Wait for any in-progress preload to complete (should be fast since it started in loader)
    startupTimer.start('file.loader.waitForPreload');
    await waitForPreload();
    startupTimer.end('file.loader.waitForPreload');

    // Get the latest state after preload completes
    const preloadState = getPreloadState();

    // Initialize any components that weren't preloaded (fallback for edge cases)
    const initTasks: Promise<void>[] = [];

    if (!preloadState.assetsReady) {
      initTasks.push(
        (async () => {
          startupTimer.start('file.loader.loadPixi');
          try {
            await loadAssets();
          } catch (error) {
            sendAnalyticsError('file.loader', 'loadPixi', error, 'Error loading pixi assets');
          }
          startupTimer.end('file.loader.loadPixi');
        })()
      );
    }

    if (!preloadState.wasmReady) {
      initTasks.push(
        (async () => {
          startupTimer.start('file.loader.initCoreClient');
          await initCoreClient();
          startupTimer.end('file.loader.initCoreClient');
        })()
      );
    }

    if (!preloadState.coreWorkersReady) {
      initCoreWorkers();
      store.set(preloadStateAtom, (prev) => ({ ...prev, coreWorkersReady: true }));
    }

    if (!preloadState.languageWorkersReady) {
      initTasks.push(preloadLanguageWorkers());
    }

    if (initTasks.length > 0) {
      await Promise.all(initTasks);
    }
  };

  try {
    // Run file API call and preload wait in parallel
    // The file API call is independent and can proceed while we wait for preload
    const [data] = await Promise.all([loadFileFromApi(), ensurePreloadComplete(), fileViewState.init(uuid)]);

    // Check if a newer load has started - if so, abort this one
    if (isStaleLoad()) {
      if (debugFlag('debugShowFileIO')) {
        console.log(`[loadFile] Aborting stale load for ${uuid}, newer load in progress`);
      }
      return { success: false, error: 'Load superseded by newer request' };
    }

    // If we got null, we need to redirect to login
    if (data === null) {
      store.set(fileLoadingAtom, defaultFileLoadingState);
      return {
        success: false,
        redirectUrl: requestUrl ? ROUTES.LOGIN_WITH_REDIRECT(requestUrl) : ROUTES.LOGIN,
      };
    }

    // Determine checkpoint to load
    let checkpoint = {
      url: data.file.lastCheckpointDataUrl,
      version: data.file.lastCheckpointVersion,
      sequenceNumber: data.file.lastCheckpointSequenceNumber,
    };

    if (isVersionHistoryPreview && sequenceNumber !== undefined) {
      const checkpointData = await apiClient.files.checkpoints.getBySequenceNumber(uuid, sequenceNumber);
      checkpoint = {
        url: checkpointData.dataUrl,
        version: checkpointData.version,
        sequenceNumber: checkpointData.sequenceNumber,
      };
    }

    // Load into core
    startupTimer.start('file.loader.quadraticCore.load');
    const result = await quadraticCore.load({
      fileId: uuid,
      teamUuid: data.team.uuid,
      url: checkpoint.url,
      version: checkpoint.version,
      sequenceNumber: checkpoint.sequenceNumber,
      noMultiplayer: isNoMultiplayer,
    });
    startupTimer.end('file.loader.quadraticCore.load');

    // Check again after core load - this is another async operation where switching could occur
    if (isStaleLoad()) {
      if (debugFlag('debugShowFileIO')) {
        console.log(`[loadFile] Aborting stale load for ${uuid} after core load, newer load in progress`);
      }
      return { success: false, error: 'Load superseded by newer request' };
    }

    if (result.error) {
      if (!isVersionHistoryPreview) {
        captureEvent({
          message: `Failed to deserialize file ${uuid} from server.`,
          extra: { error: result.error },
        });
        updateRecentFiles(uuid, data.file.name, false);
      }
      store.set(fileLoadingAtom, {
        uuid,
        status: 'error',
        data: null,
        error: result.error,
        isVersionHistoryPreview,
        sequenceNumber,
      });
      store.set(fileLoadingProgressAtom, null);
      return { success: false, error: result.error };
    }

    if (result.version) {
      // Check version compatibility
      if (compareVersions(result.version, data.file.lastCheckpointVersion) === VersionComparisonResult.LessThan) {
        if (!isVersionHistoryPreview) {
          captureEvent({
            message: `User opened a file at version ${result.version} but the app is at version ${data.file.lastCheckpointVersion}. The app will automatically reload.`,
            level: 'log',
          });
          updateRecentFiles(uuid, data.file.name, false);
        }
        window.location.reload();
        return { success: false };
      }

      // Set thumbnail dirty if needed
      if (
        !isVersionHistoryPreview &&
        !data.file.thumbnail &&
        data.userMakingRequest.filePermissions.includes('FILE_EDIT')
      ) {
        thumbnail.setThumbnailDirty();
      }
    }

    // Update recent files
    if (!isVersionHistoryPreview) {
      updateRecentFiles(uuid, data.file.name, true);
    }

    // Prepare final data with permission modifications
    let finalData = { ...data };

    // Make read-only for version history preview
    if (isVersionHistoryPreview) {
      finalData = {
        ...finalData,
        userMakingRequest: {
          ...finalData.userMakingRequest,
          filePermissions: [FilePermissionSchema.enum.FILE_VIEW],
        },
      };
    }

    // When noMultiplayer is enabled, allow editing since changes are local only
    if (isNoMultiplayer && !finalData.userMakingRequest.filePermissions.includes(FilePermissionSchema.enum.FILE_EDIT)) {
      finalData = {
        ...finalData,
        userMakingRequest: {
          ...finalData.userMakingRequest,
          filePermissions: [...finalData.userMakingRequest.filePermissions, FilePermissionSchema.enum.FILE_EDIT],
        },
      };
    }

    // Set up analytics and billing
    registerEventAnalyticsData({ isOnPaidPlan: finalData.team.isOnPaidPlan });
    updateTeamBilling({
      isOnPaidPlan: finalData.team.isOnPaidPlan,
      planType: finalData.team.planType ?? 'FREE',
    });
    handleSentryReplays(finalData.team.settings.analyticsAi);

    // Seed the user's clientDataKv into its dedicated atom (no-op if already
    // loaded in this session, e.g. when coming from the dashboard).
    await loadClientDataKv();

    startupTimer.end('file.loader');
    startupTimer.show();

    // Set loaded state
    store.set(fileLoadingAtom, {
      uuid,
      status: 'loaded',
      data: finalData,
      error: null,
      isVersionHistoryPreview,
      sequenceNumber,
    });
    store.set(fileLoadingProgressAtom, null);

    return { success: true };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error loading file';
    store.set(fileLoadingAtom, {
      uuid,
      status: 'error',
      data: null,
      error: errorMessage,
      isVersionHistoryPreview,
      sequenceNumber,
    });
    store.set(fileLoadingProgressAtom, null);
    return { success: false, error: errorMessage };
  }
};

/**
 * Updates the progress of the in-flight file load. No-op if the atom is not
 * currently in the 'loading' state (e.g. progress event arrives after the
 * load completed or was superseded).
 */
export const setFileLoadingProgress = (loaded: number, total: number | null): void => {
  const store = getDefaultStore();
  if (store.get(fileLoadingAtom).status !== 'loading') return;
  store.set(fileLoadingProgressAtom, { loaded, total });
};

/**
 * Gets the current file loading state.
 */
export const getFileLoadingState = (): FileLoadingState => {
  return getDefaultStore().get(fileLoadingAtom);
};

/**
 * Checks if a specific file is already loaded.
 */
export const isFileLoaded = (uuid: string): boolean => {
  const state = getFileLoadingState();
  return state.uuid === uuid && state.status === 'loaded';
};

/**
 * Resets the file loading state to empty.
 * Called when unloading a file (e.g., navigating away).
 */
export const resetFileLoadingState = (): void => {
  const store = getDefaultStore();
  store.set(fileLoadingAtom, defaultFileLoadingState);
  store.set(fileLoadingProgressAtom, null);
};

/**
 * Loads a specific version of the currently loaded file, or reloads the
 * current version when called without a sequenceNumber.
 */
export const loadFileVersion = (sequenceNumber?: number): Promise<LoadFileResult> => {
  const state = getFileLoadingState();
  if (!state.uuid) {
    return Promise.resolve({ success: false, error: 'No file loaded' });
  }
  return loadFile({ uuid: state.uuid, sequenceNumber });
};
